// Envio de template proprio: escolhe a imagem (PNG/JPEG/WebP ate 5 MB),
// recorta em 9:16 com zoom e posicao, da um nome e sobe o JPEG 1080x1920.

import { useEffect, useRef, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { STORY_HEIGHT, STORY_WIDTH } from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUploadStoryTemplate } from "@/lib/hooks/useStories";
import { MAX_ZOOM, checkTemplateFile, cropRect } from "@/lib/stories";

interface Props {
  readonly tenantId: string | null;
  readonly disabled: boolean;
  readonly onDone: (message: string, ok: boolean) => void;
}

interface Crop {
  zoom: number;
  x: number;
  y: number;
}

const PREVIEW_W = 180;

function drawCrop(canvas: HTMLCanvasElement, image: HTMLImageElement, crop: Crop): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const r = cropRect(image.naturalWidth, image.naturalHeight, crop.zoom, crop.x, crop.y);
  ctx.drawImage(image, r.sx, r.sy, r.sw, r.sh, 0, 0, canvas.width, canvas.height);
}

function toJpeg(image: HTMLImageElement, crop: Crop): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = STORY_WIDTH;
  canvas.height = STORY_HEIGHT;
  drawCrop(canvas, image, crop);
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Não foi possível recortar a imagem."))), "image/jpeg", 0.9),
  );
}

function CropSliders({ crop, onChange }: { crop: Crop; onChange: (c: Crop) => void }): JSX.Element {
  const slider = (label: string, key: keyof Crop, min: number, max: number, step: number): JSX.Element => (
    <label className="flex items-center gap-2 text-xs text-muted-foreground">
      <span className="w-20">{label}</span>
      <input type="range" min={min} max={max} step={step} value={crop[key]} onChange={(e) => onChange({ ...crop, [key]: Number(e.target.value) })} className="flex-1" />
    </label>
  );
  return (
    <div className="space-y-2">
      {slider("Zoom", "zoom", 1, MAX_ZOOM, 0.05)}
      {slider("Horizontal", "x", -1, 1, 0.02)}
      {slider("Vertical", "y", -1, 1, 0.02)}
    </div>
  );
}

export function StoryTemplateUpload({ tenantId, disabled, onDone }: Props): JSX.Element {
  const upload = useUploadStoryTemplate(tenantId);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [name, setName] = useState("");
  const [crop, setCrop] = useState<Crop>({ zoom: 1, x: 0, y: 0 });
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (image && canvasRef.current) drawCrop(canvasRef.current, image, crop);
  }, [image, crop]);

  function pick(file: File | undefined): void {
    if (!file) return;
    const check = checkTemplateFile(file);
    if (!check.ok) {
      onDone(check.message, false);
      return;
    }
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      setImage(img);
      setCrop({ zoom: 1, x: 0, y: 0 });
      setName((prev) => prev || file.name.replace(/\.[^.]+$/, "").slice(0, 60));
    };
    img.onerror = () => onDone("Não foi possível abrir essa imagem.", false);
    img.src = URL.createObjectURL(file);
  }

  async function save(): Promise<void> {
    if (!image) return;
    try {
      await upload.mutateAsync({ name: name.trim(), blob: await toJpeg(image, crop) });
      setImage(null);
      setName("");
      onDone("Template enviado.", true);
    } catch (e) {
      onDone(e instanceof Error ? e.message : "Não foi possível enviar o template.", false);
    }
  }

  return (
    <div className="space-y-3">
      <Label htmlFor="story-file" variant="field">Enviar imagem (PNG, JPEG ou WebP até 5 MB)</Label>
      <Input id="story-file" type="file" accept="image/png,image/jpeg,image/webp" disabled={disabled} onChange={(e) => pick(e.target.files?.[0])} />
      {image && (
        <div className="flex flex-col gap-4 sm:flex-row">
          <canvas ref={canvasRef} width={PREVIEW_W} height={PREVIEW_W * (STORY_HEIGHT / STORY_WIDTH)} className="rounded-md border border-border" aria-label="Recorte 9:16" />
          <div className="flex-1 space-y-3">
            <CropSliders crop={crop} onChange={setCrop} />
            <Input aria-label="Nome do template" placeholder="Nome do template" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
            <div className="flex gap-2">
              <Button type="button" disabled={!name.trim() || upload.isPending} onClick={() => void save()}>
                {upload.isPending ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Salvar template
              </Button>
              <Button type="button" variant="ghost" onClick={() => setImage(null)}>
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
